<template>
  <v-navigation-drawer
    :value="value"
    fixed
    right
    width="860"
    height="100vh"
    class="supply-history-drawer"
    @input="$emit('input', $event)"
  >
    <div class="drawer-shell" @click.stop>
      <div class="drawer-head">
        <div class="drawer-title-wrap">
          <button
            v-if="view !== 'list'"
            type="button"
            class="drawer-back"
            @click="back"
          >
            ← {{ '목록' | translate }}
          </button>
          <div class="drawer-title">{{ headerTitle }}</div>
          <div v-if="subtitleText" class="drawer-sub" :title="subtitleText">{{ subtitleText }}</div>
        </div>
        <div class="drawer-head-actions">
          <button type="button" class="drawer-close" @click="close">×</button>
        </div>
      </div>

      <!-- 1) 대상 목록 -->
      <div v-if="view === 'list'" class="drawer-body">
        <div v-if="loading" class="drawer-empty">{{ '조회 중...' | translate }}</div>
        <div v-else-if="!listItems.length" class="drawer-empty">
          {{ emptyListMessage }}
        </div>

        <!-- 교체 이력 모드 -->
        <template v-else-if="mode === 'history'">
          <table class="drawer-table">
            <thead>
              <tr>
                <th class="nowrap sortable" @click="sortListBy('collectedDatetime')">
                  {{ '교체일시' | translate }}{{ listSortMark('collectedDatetime') }}
                </th>
                <th class="sortable" @click="sortListBy('rentalcompanyName')">
                  {{ '임대업체' | translate }}{{ listSortMark('rentalcompanyName') }}
                </th>
                <th class="sortable" @click="sortListBy('accountName')">
                  {{ '거래처' | translate }}{{ listSortMark('accountName') }}
                </th>
                <th class="sortable" @click="sortListBy('printerLabel')">
                  {{ '프린터' | translate }}{{ listSortMark('printerLabel') }}
                </th>
                <th class="sortable" @click="sortListBy('supplyDesc')">
                  {{ '소모품' | translate }}{{ listSortMark('supplyDesc') }}
                </th>
                <th class="nowrap sortable" @click="sortListBy('remainPercent')">
                  {{ '잔량' | translate }}{{ listSortMark('remainPercent') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in sortedListItems"
                :key="item.monitoringPrinterSupplyHistId"
                class="clickable"
                @click="openHistoryDetail(item)"
              >
                <td class="col-datetime">{{ item.collectedDatetime || '-' }}</td>
                <td :title="item.rentalcompanyName">{{ item.rentalcompanyName || '-' }}</td>
                <td :title="item.accountName">{{ item.accountName || '-' }}</td>
                <td :title="printerLabel(item)">{{ printerLabel(item) }}</td>
                <td class="desc-cell">
                  <span class="desc-ellipsis" :title="supplyDesc(item)">
                    {{ supplyDesc(item) }}
                  </span>
                </td>
                <td class="col-remain">{{ formatRemain(item) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="drawer-paging">
            <button
              type="button"
              class="page-btn"
              :disabled="page <= 1 || loading"
              @click="changePage(page - 1)"
            >
              {{ '이전' | translate }}
            </button>
            <span>{{ page }} / {{ totalPages }}</span>
            <button
              type="button"
              class="page-btn"
              :disabled="page >= totalPages || loading"
              @click="changePage(page + 1)"
            >
              {{ '다음' | translate }}
            </button>
          </div>
        </template>

        <!-- 슬롯 상세 (SKU 슬롯수/사용 목록) -->
        <div v-else-if="isSlotDetail">
          <table class="drawer-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortListBy('rentalcompanyName')">
                  {{ '임대업체' | translate }}{{ listSortMark('rentalcompanyName') }}
                </th>
                <th class="sortable" @click="sortListBy('accountName')">
                  {{ '거래처' | translate }}{{ listSortMark('accountName') }}
                </th>
                <th class="sortable" @click="sortListBy('printerLabel')">
                  {{ '프린터' | translate }}{{ listSortMark('printerLabel') }}
                </th>
                <th class="sortable" @click="sortListBy('itemLabel')">
                  {{ '소모품' | translate }}{{ listSortMark('itemLabel') }}
                </th>
                <th class="nowrap sortable" @click="sortListBy('statusCode')">
                  {{ '상태' | translate }}{{ listSortMark('statusCode') }}
                </th>
                <th class="nowrap sortable" @click="sortListBy('remainPercent')">
                  {{ '잔량' | translate }}{{ listSortMark('remainPercent') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in sortedListItems" :key="item.monitoringPrinterSupplyId || item.groupKey">
                <td :title="item.rentalcompanyName">{{ item.rentalcompanyName || '-' }}</td>
                <td :title="item.accountName">{{ item.accountName || '-' }}</td>
                <td :title="printerLabel(item)">{{ printerLabel(item) }}</td>
                <td class="desc-cell">
                  <span class="desc-ellipsis" :title="itemLabel(item)">{{ itemLabel(item) }}</span>
                </td>
                <td class="nowrap">{{ item.statusCode || '-' }}</td>
                <td class="col-remain">{{ formatRemain(item) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="drawer-paging">
            <button
              type="button"
              class="page-btn"
              :disabled="page <= 1 || loading"
              @click="changePage(page - 1)"
            >
              {{ '이전' | translate }}
            </button>
            <span>{{ page }} / {{ totalPages }}</span>
            <button
              type="button"
              class="page-btn"
              :disabled="page >= totalPages || loading"
              @click="changePage(page + 1)"
            >
              {{ '다음' | translate }}
            </button>
          </div>
        </div>

        <div v-else-if="mode === 'sku'" class="drawer-table-scroll">
        <table class="drawer-table">
          <thead>
            <tr>
              <th>{{ skuColumnLabel }}</th>
              <th v-if="skuGroupBy === 'MODEL'">{{ '제조사' | translate }}</th>
              <th>{{ '프린터 수' | translate }}</th>
              <th>{{ '슬롯' | translate }}</th>
              <th>{{ 'LOW/EMPTY' | translate }}</th>
              <th>{{ '긴급' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in listItems"
              :key="'sku-' + (item.groupKey || item.partNumber || idx)"
              class="clickable"
              @click="openSkuRowDetail(item)"
            >
              <td :title="skuRowLabel(item)">{{ skuRowLabel(item) }}</td>
              <td v-if="skuGroupBy === 'MODEL'">{{ item.manufacturerName || '-' }}</td>
              <td>{{ item.printerCount | currency }}</td>
              <td>{{ item.activeCount | currency }}</td>
              <td>{{ (Number(item.lowCount || 0) + Number(item.emptyCount || 0)) | currency }}</td>
              <td>{{ item.urgentCount | currency }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!loading && listItems.length" class="drawer-paging">
          <button type="button" class="page-btn" :disabled="page <= 1 || loading" @click="changePage(page - 1)">
            {{ '이전' | translate }}
          </button>
          <span>{{ page }} / {{ totalPages }}</span>
          <button type="button" class="page-btn" :disabled="page >= totalPages || loading" @click="changePage(page + 1)">
            {{ '다음' | translate }}
          </button>
        </div>
        </div>

        <!-- LOW/EMPTY 모드 -->
        <table v-else-if="mode === 'lowEmpty'" class="drawer-table">
          <thead>
            <tr>
              <th class="sortable" @click="sortListBy('itemLabel')">
                {{ '소모품' | translate }}{{ listSortMark('itemLabel') }}
              </th>
              <th class="sortable" @click="sortListBy('lowCount')">
                {{ 'LOW' | translate }}{{ listSortMark('lowCount') }}
              </th>
              <th class="sortable" @click="sortListBy('emptyCount')">
                {{ 'EMPTY' | translate }}{{ listSortMark('emptyCount') }}
              </th>
              <th class="sortable" @click="sortListBy('lowEmptyPrinterCount')">
                {{ '프린터' | translate }}{{ listSortMark('lowEmptyPrinterCount') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in sortedListItems"
              :key="'le-' + idx"
              class="clickable"
              @click="openItemWorkspace(item, 'lowEmpty')"
            >
              <td :title="itemLabel(item)">{{ itemLabel(item) }}</td>
              <td class="num-cell" @click.stop>
                <button type="button" class="num-link" :disabled="!Number(item.lowCount || 0)" @click.stop="openLowEmptySlots(item, ['LOW'])">
                  {{ item.lowCount | currency }}
                </button>
              </td>
              <td class="num-cell" @click.stop>
                <button type="button" class="num-link" :disabled="!Number(item.emptyCount || 0)" @click.stop="openLowEmptySlots(item, ['EMPTY'])">
                  {{ item.emptyCount | currency }}
                </button>
              </td>
              <td class="num-cell" @click.stop>
                <button
                  type="button"
                  class="num-link"
                  :disabled="!Number(item.lowEmptyPrinterCount || 0)"
                  @click.stop="openLowEmptySlots(item, ['LOW', 'EMPTY'], { distinctPrinter: true })"
                >
                  {{ item.lowEmptyPrinterCount | currency }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 예측 모드 -->
        <table v-else class="drawer-table">
          <thead>
            <tr>
              <th class="sortable" @click="sortListBy('itemLabel')">
                {{ '소모품' | translate }}{{ listSortMark('itemLabel') }}
              </th>
              <th class="sortable" @click="sortListBy('replaceCount')">
                {{ '교체수' | translate }}{{ listSortMark('replaceCount') }}
              </th>
              <th class="sortable" @click="sortListBy('monthlyForecast')">
                {{ '월간예측' | translate }}{{ listSortMark('monthlyForecast') }}
              </th>
              <th class="sortable" @click="sortListBy('avgCycleDays')">
                {{ '평균주기' | translate }}{{ listSortMark('avgCycleDays') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in sortedListItems"
              :key="'fc-' + idx"
              class="clickable"
              @click="openItemWorkspace(item, 'forecast')"
            >
              <td :title="itemLabel(item)">{{ itemLabel(item) }}</td>
              <td>{{ item.replaceCount | currency }}</td>
              <td>{{ formatForecast(item.monthlyForecast) }}</td>
              <td>{{ formatForecast(item.avgCycleDays) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="view === 'lowEmptySlots'" class="drawer-body">
        <div v-if="loading" class="drawer-empty">{{ '조회 중...' | translate }}</div>
        <div v-else-if="!listItems.length" class="drawer-empty">
          {{ (slotFocus && slotFocus.distinctPrinter ? '대상 프린터가 없습니다.' : '대상 슬롯이 없습니다.') | translate }}
        </div>
        <div v-else class="drawer-table-scroll">
          <table class="drawer-table">
            <thead>
              <tr>
                <th>{{ '거래처' | translate }}</th>
                <th>{{ '프린터' | translate }}</th>
                <th>{{ '소모품' | translate }}</th>
                <th>{{ '상태' | translate }}</th>
                <th>{{ '잔량' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in listItems" :key="(item.monitoringPrinterId || '') + '-' + (item.monitoringPrinterSupplyId || item.groupKey)">
                <td>
                  <button type="button" class="cell-link" :disabled="!item.accountId" @click.stop="$emit('open-account', item)">
                    {{ item.accountName || '-' }}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="cell-link"
                    :disabled="!item.tnDeviceId || !item.rentalcompanyId"
                    :title="printerLabel(item)"
                    @click.stop="$emit('open-printer', item)"
                  >
                    {{ printerLabel(item) }}
                  </button>
                </td>
                <td>{{ itemLabel(item) }}</td>
                <td>{{ item.statusCode || '-' }}</td>
                <td>{{ formatRemain(item) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="listTotalCount > rows" class="drawer-paging">
            <button type="button" class="page-btn" :disabled="page <= 1 || loading" @click="changePage(page - 1)">
              {{ '이전' | translate }}
            </button>
            <span>{{ page }} / {{ totalPages }}</span>
            <button type="button" class="page-btn" :disabled="page >= totalPages || loading" @click="changePage(page + 1)">
              {{ '다음' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- 2) 품목 워크스페이스: 요약 + 교체 이력 (클릭 1회에 통합) -->
      <div v-else-if="view === 'itemWorkspace'" class="drawer-body detail">
        <div v-if="!selectedItem" class="drawer-empty">
          {{ '선택된 항목이 없습니다.' | translate }}
        </div>
        <template v-else>
          <dl class="detail-list">
            <div class="detail-row">
              <dt>{{ '소모품' | translate }}</dt>
              <dd>{{ itemLabel(selectedItem) }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '카테고리' | translate }}</dt>
              <dd>{{ formatCategory(selectedItem.supplyCategory) }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '품번' | translate }}</dt>
              <dd>{{ selectedItem.partNumber || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '색상' | translate }}</dt>
              <dd>{{ selectedItem.colorCode || '-' }}</dd>
            </div>
            <template v-if="workspaceKind === 'lowEmpty'">
              <div class="detail-row">
                <dt>LOW / EMPTY</dt>
                <dd>
                  {{ selectedItem.lowCount | currency }} /
                  {{ selectedItem.emptyCount | currency }}
                </dd>
              </div>
              <div class="detail-row">
                <dt>{{ 'LOW/EMPTY 프린터' | translate }}</dt>
                <dd>{{ selectedItem.lowEmptyPrinterCount | currency }}</dd>
              </div>
            </template>
            <template v-else>
              <div class="detail-row">
                <dt>{{ '교체수' | translate }}</dt>
                <dd>{{ selectedItem.replaceCount | currency }}</dd>
              </div>
              <div class="detail-row">
                <dt>{{ '월간예측' | translate }}</dt>
                <dd>{{ formatForecast(selectedItem.monthlyForecast) }}</dd>
              </div>
              <div class="detail-row">
                <dt>{{ '평균주기(일)' | translate }}</dt>
                <dd>{{ formatForecast(selectedItem.avgCycleDays) }}</dd>
              </div>
              <div class="detail-row">
                <dt>{{ '최근교체' | translate }}</dt>
                <dd>{{ selectedItem.lastReplacedDatetime || '-' }}</dd>
              </div>
            </template>
          </dl>

          <div class="section-title">{{ '교체 이력' | translate }}</div>
          <div v-if="historyLoading" class="drawer-empty compact">
            {{ '조회 중...' | translate }}
          </div>
          <div v-else-if="!historyItems.length" class="drawer-empty compact">
            {{ '교체 이력이 없습니다.' | translate }}
          </div>
          <template v-else>
            <table class="drawer-table">
              <thead>
                <tr>
                  <th class="sortable" @click="sortHistoryBy('collectedDatetime')">
                    {{ '교체일시' | translate }}{{ historySortMark('collectedDatetime') }}
                  </th>
                  <th class="sortable" @click="sortHistoryBy('accountName')">
                    {{ '거래처' | translate }}{{ historySortMark('accountName') }}
                  </th>
                  <th class="sortable" @click="sortHistoryBy('printerLabel')">
                    {{ '프린터' | translate }}{{ historySortMark('printerLabel') }}
                  </th>
                  <th class="sortable" @click="sortHistoryBy('remainPercent')">
                    {{ '잔량' | translate }}{{ historySortMark('remainPercent') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in sortedHistoryItems"
                  :key="'ws-' + item.monitoringPrinterSupplyHistId"
                  class="clickable"
                  @click="openHistoryDetail(item)"
                >
                  <td>{{ item.collectedDatetime || '-' }}</td>
                  <td>{{ item.accountName || '-' }}</td>
                  <td>{{ printerLabel(item) }}</td>
                  <td>{{ formatRemain(item) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="drawer-paging">
              <button
                type="button"
                class="page-btn"
                :disabled="page <= 1 || historyLoading"
                @click="changeHistoryPage(page - 1)"
              >
                {{ '이전' | translate }}
              </button>
              <span>{{ page }} / {{ historyTotalPages }}</span>
              <button
                type="button"
                class="page-btn"
                :disabled="page >= historyTotalPages || historyLoading"
                @click="changeHistoryPage(page + 1)"
              >
                {{ '다음' | translate }}
              </button>
            </div>
          </template>
        </template>
      </div>

      <!-- 3) 교체 상세 -->
      <div v-else-if="view === 'historyDetail'" class="drawer-body detail">
        <div v-if="!selectedHistory" class="drawer-empty">
          {{ '선택된 이력이 없습니다.' | translate }}
        </div>
        <template v-else>
          <dl class="detail-list">
            <div class="detail-row">
              <dt>{{ '교체일시' | translate }}</dt>
              <dd>{{ selectedHistory.collectedDatetime || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '거래처' | translate }}</dt>
              <dd>{{ selectedHistory.accountName || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '프린터 모델' | translate }}</dt>
              <dd>{{ selectedHistory.printerModelName || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '시리얼' | translate }}</dt>
              <dd>{{ selectedHistory.printerSerialNo || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '카테고리' | translate }}</dt>
              <dd>{{ formatCategory(selectedHistory.supplyCategory) }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '설명' | translate }}</dt>
              <dd>{{ selectedHistory.description || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '변경유형' | translate }}</dt>
              <dd>{{ selectedHistory.changeType || '-' }}</dd>
            </div>
            <div class="detail-row">
              <dt>{{ '교체 원인' | translate }}</dt>
              <dd>{{ formatChangeReason(selectedHistory) }}</dd>
            </div>
          </dl>
          <div class="compare-grid">
            <section class="compare-card before">
              <div class="compare-title">{{ '교체 전' | translate }}</div>
              <div v-if="!hasBeforeCollect(selectedHistory)" class="compare-empty">
                {{ '직전 수집 이력이 없습니다.' | translate }}
              </div>
              <dl v-else class="detail-list compact">
                <div class="detail-row">
                  <dt>{{ '수집일시' | translate }}</dt>
                  <dd>{{ selectedHistory.beforeCollectedDatetime || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '프린터 모델' | translate }}</dt>
                  <dd>{{ selectedHistory.beforePrinterModelName || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '품번' | translate }}</dt>
                  <dd>{{ selectedHistory.beforePartNumber || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '설명' | translate }}</dt>
                  <dd>{{ selectedHistory.beforeDescription || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '잔량(%)' | translate }}</dt>
                  <dd>{{ formatPercent(selectedHistory.beforeRemainPercent) }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '상태' | translate }}</dt>
                  <dd>{{ selectedHistory.beforeStatusCode || '-' }}</dd>
                </div>
              </dl>
            </section>
            <section class="compare-card after">
              <div class="compare-title">{{ '교체 후' | translate }}</div>
              <dl class="detail-list compact">
                <div class="detail-row">
                  <dt>{{ '수집일시' | translate }}</dt>
                  <dd>{{ selectedHistory.collectedDatetime || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '프린터 모델' | translate }}</dt>
                  <dd>{{ selectedHistory.printerModelName || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '품번' | translate }}</dt>
                  <dd>{{ selectedHistory.partNumber || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '설명' | translate }}</dt>
                  <dd>{{ selectedHistory.description || '-' }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '잔량(%)' | translate }}</dt>
                  <dd>{{ formatPercent(selectedHistory.remainPercent) }}</dd>
                </div>
                <div class="detail-row">
                  <dt>{{ '상태' | translate }}</dt>
                  <dd>{{ selectedHistory.statusCode || '-' }}</dd>
                </div>
              </dl>
            </section>
          </div>
        </template>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { PATH } from '@/constants'
import NumberFormatUtil from '@/assets/plugins/mps-common/formatters/NumberFormatUtil'
import {
  downloadSupplyExcel,
  sortSupplyRows,
  supplyExcelStamp,
} from '@/excel/downloadSupplyExcel'

export default {
  name: 'SupplyReplaceHistoryDrawer',
  mixins: [NumberFormatUtil],
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    /** history | lowEmpty | forecast */
    mode: { type: String, default: 'history' },
    query: { type: Object, default: () => ({}) },
    showExcel: { type: Boolean, default: true },
  },
  data() {
    return {
      view: 'list', // list | itemWorkspace | historyDetail | lowEmptySlots
      slotFocus: null,
      savedLowEmptyList: null,
      workspaceKind: 'forecast',
      loading: false,
      historyLoading: false,
      listItems: [],
      historyItems: [],
      listTotalCount: 0,
      historyTotalCount: 0,
      page: 1,
      rows: 30,
      selectedItem: null,
      selectedHistory: null,
      outsideCloseBound: null,
      listSort: { key: 'collectedDatetime', dir: 'DESC' },
      historySort: { key: 'collectedDatetime', dir: 'DESC' },
      excelBusy: false,
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.listTotalCount || 0) / this.rows))
    },
    historyTotalPages() {
      return Math.max(1, Math.ceil((this.historyTotalCount || 0) / this.rows))
    },
    headerTitle() {
      if (this.view === 'lowEmptySlots') {
        const n = Number(this.listTotalCount || 0).toLocaleString()
        if (this.slotFocus && this.slotFocus.groupDetail) {
          const item = this.slotFocus.item || {}
          return `${this.skuRowLabel(item)} (${n})`
        }
        if (this.slotFocus && this.slotFocus.distinctPrinter) {
          return `${this.t('프린터')} (${n})`
        }
        const which = (this.slotFocus && this.slotFocus.statusCodes || []).join('/')
        return `${which || 'LOW/EMPTY'} (${n})`
      }
      if (this.view === 'list' && this.mode === 'sku' && !this.loading) {
        return `${this.skuCountLabel()} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'list' && this.mode === 'lowEmpty' && this.isSlotDetail && !this.loading) {
        return `${this.t('LOW/EMPTY 슬롯')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'list' && this.mode === 'lowEmpty' && !this.loading) {
        return `${this.t('LOW/EMPTY 품목')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'list' && this.mode === 'forecast' && !this.loading) {
        return `${this.t('예측 가능 품목')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'list' && this.isSlotDetail && this.query.distinctPrinter && !this.loading) {
        return `${this.t('프린터')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'list' && this.isSlotDetail && this.mode === 'error' && !this.loading) {
        return `${this.t('에러 슬롯')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (
        this.view === 'list' &&
        this.mode === 'history' &&
        !this.isSlotDetail &&
        !this.loading &&
        !this.query.supplyCategory &&
        !this.query.manufacturerName &&
        !this.query.monitoringPrinterSupplyId &&
        !this.query.monitoringPrinterId &&
        !this.query.accountId &&
        !this.query.printerModelId &&
        !this.query.partNumberExact &&
        !this.query.groupKey &&
        !this.query.marketItemKey &&
        !this.query.keyword
      ) {
        return `${this.t('기간 총 교체')} (${Number(this.listTotalCount || 0).toLocaleString()})`
      }
      if (this.view === 'historyDetail') return this.t('교체 상세')
      if (this.view === 'itemWorkspace') {
        return this.workspaceKind === 'lowEmpty'
          ? this.t('LOW/EMPTY 상세')
          : this.t('예측 품목 상세')
      }
      if (this.isSlotDetail) return this.title || this.t('소모품 사용 목록')
      if (this.mode === 'lowEmpty') return this.t('LOW/EMPTY 소모품')
      if (this.mode === 'forecast') return this.t('예측 가능 품목')
      return this.t('소모품 교체 내역')
    },
    subtitleText() {
      if (this.view === 'itemWorkspace' && this.selectedItem) {
        const label = this.itemLabel(this.selectedItem)
        const head = String(this.headerTitle || '').trim()
        return label && label !== head ? label : ''
      }
      return ''
    },
    emptyListMessage() {
      if (this.isSlotDetail && this.query.distinctPrinter) return this.t('대상 프린터가 없습니다.')
      if (this.isSlotDetail) return this.t('해당 소모품 슬롯이 없습니다.')
      if (this.mode === 'sku') return this.t('품번이 없습니다.')
      if (this.mode === 'lowEmpty') return this.t('LOW/EMPTY 소모품이 없습니다.')
      if (this.mode === 'forecast') return this.t('예측 가능 품목이 없습니다.')
      return this.t('교체 이력이 없습니다.')
    },
    isSlotDetail() {
      return this.query && this.query.slotDetail === true
    },
    skuGroupBy() {
      return (this.query && this.query.groupBy) || 'SUPPLY'
    },
    skuColumnLabel() {
      if (this.skuGroupBy === 'MODEL') return this.t('프린터 모델')
      if (this.skuGroupBy === 'ACCOUNT') return this.t('거래처')
      if (this.skuGroupBy === 'RENTAL') return this.t('임대업체')
      return this.t('품번')
    },
    excelDisabled() {
      if (this.view === 'itemWorkspace') return !this.historyItems.length
      return !this.listItems.length
    },
    sortGetters() {
      return {
        printerLabel: (row) => this.printerLabel(row),
        itemLabel: (row) => this.itemLabel(row),
        supplyDesc: (row) => this.supplyDesc(row),
      }
    },
    sortedListItems() {
      return sortSupplyRows(this.listItems, this.listSort, this.sortGetters)
    },
    sortedHistoryItems() {
      return sortSupplyRows(this.historyItems, this.historySort, this.sortGetters)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetToList()
        this.bindOutsideClose()
        this.loadList()
      } else {
        this.unbindOutsideClose()
      }
    },
    mode() {
      if (this.value) {
        this.resetToList()
        this.loadList()
      }
    },
    query: {
      deep: true,
      handler() {
        if (this.value && this.view === 'list' && this.mode === 'history') {
          this.page = 1
          this.loadList()
        }
      },
    },
  },
  beforeDestroy() {
    this.unbindOutsideClose()
  },
  methods: {
    t(key) {
      return this.$translate ? this.$translate(key) : key
    },
    defaultListSort() {
      if (this.query && this.query.slotDetail === true) {
        return { key: 'remainPercent', dir: 'ASC' }
      }
      if (this.mode === 'lowEmpty') return { key: 'emptyCount', dir: 'DESC' }
      if (this.mode === 'forecast') return { key: 'replaceCount', dir: 'DESC' }
      return { key: 'collectedDatetime', dir: 'DESC' }
    },
    isNumericSortKey(key) {
      return [
        'replaceCount',
        'remainPercent',
        'lowCount',
        'emptyCount',
        'lowEmptyPrinterCount',
        'monthlyForecast',
        'avgCycleDays',
        'collectedDatetime',
      ].includes(key)
    },
    toggleSort(target, key) {
      const sort = this[target]
      if (sort.key === key) {
        sort.dir = sort.dir === 'ASC' ? 'DESC' : 'ASC'
        return
      }
      this[target] = { key, dir: this.isNumericSortKey(key) ? 'DESC' : 'ASC' }
    },
    sortListBy(key) {
      this.toggleSort('listSort', key)
      this.page = 1
      this.loadList()
    },
    sortHistoryBy(key) {
      this.toggleSort('historySort', key)
      this.page = 1
      this.loadItemHistory()
    },
    listSortMark(key) {
      if (this.listSort.key !== key) return ''
      return this.listSort.dir === 'ASC' ? ' ▲' : ' ▼'
    },
    historySortMark(key) {
      if (this.historySort.key !== key) return ''
      return this.historySort.dir === 'ASC' ? ' ▲' : ' ▼'
    },
    mapSortKey(key) {
      const map = {
        printerLabel: 'printerModelName',
        supplyDesc: 'description',
        itemLabel: 'supplyLabel',
      }
      return map[key] || key
    },
    historyOrderPayload(sort) {
      const allowed = [
        'collectedDatetime',
        'accountName',
        'rentalcompanyName',
        'printerModelName',
        'description',
        'remainPercent',
        'partNumber',
      ]
      const mapped = this.mapSortKey(sort.key)
      return {
        orderBy: allowed.includes(mapped) ? mapped : 'collectedDatetime',
        orderDir: sort.dir,
      }
    },
    async downloadExcel() {
      if (this.excelBusy) return
      this.excelBusy = true
      try {
        const title = this.headerTitle || this.t('소모품 교체 현황')
        const rows =
          this.view === 'itemWorkspace'
            ? await this.fetchAllHistoryRows()
            : await this.fetchAllListRows()
        if (!rows.length) {
          this.$toasted?.error?.(this.t('내려받을 데이터가 없습니다.'))
          return
        }
        await downloadSupplyExcel({
          title,
          fileName: `${title}_${supplyExcelStamp()}.xlsx`,
          columns: this.excelColumns(),
          rows,
        })
      } catch (e) {
        console.error(e)
        this.$toasted?.error?.(this.t('엑셀 다운로드에 실패하였습니다.'))
      } finally {
        this.excelBusy = false
      }
    },
    excelColumns() {
      if (this.view === 'itemWorkspace' || (this.mode === 'history' && !this.isSlotDetail)) {
        return [
          { header: this.t('교체일시'), key: 'collectedDatetime', width: 22 },
          { header: this.t('임대업체'), key: 'rentalcompanyName', width: 18 },
          { header: this.t('거래처'), key: 'accountName', width: 20 },
          {
            header: this.t('프린터'),
            key: 'printer',
            width: 28,
            value: (row) => this.printerLabel(row),
          },
          {
            header: this.t('소모품'),
            key: 'supply',
            width: 32,
            value: (row) =>
              this.view === 'itemWorkspace' ? this.itemLabel(row) : this.supplyDesc(row),
          },
          {
            header: this.t('잔량'),
            key: 'remainPercent',
            width: 10,
            value: (row) => this.formatRemain(row),
          },
        ]
      }
      if (this.isSlotDetail) {
        return [
          { header: this.t('임대업체'), key: 'rentalcompanyName', width: 18 },
          { header: this.t('거래처'), key: 'accountName', width: 20 },
          {
            header: this.t('프린터'),
            key: 'printer',
            width: 28,
            value: (row) => this.printerLabel(row),
          },
          {
            header: this.t('소모품'),
            key: 'supply',
            width: 32,
            value: (row) => this.itemLabel(row),
          },
          { header: this.t('상태'), key: 'statusCode', width: 12 },
          {
            header: this.t('잔량'),
            key: 'remainPercent',
            width: 10,
            value: (row) => this.formatRemain(row),
          },
        ]
      }
      if (this.mode === 'lowEmpty') {
        return [
          {
            header: this.t('소모품'),
            key: 'supply',
            width: 32,
            value: (row) => this.itemLabel(row),
          },
          { header: this.t('LOW'), key: 'lowCount', width: 10 },
          { header: this.t('EMPTY'), key: 'emptyCount', width: 10 },
          { header: this.t('프린터'), key: 'lowEmptyPrinterCount', width: 12 },
        ]
      }
      return [
        {
          header: this.t('소모품'),
          key: 'supply',
          width: 32,
          value: (row) => this.itemLabel(row),
        },
        { header: this.t('교체수'), key: 'replaceCount', width: 12 },
        { header: this.t('월간예측'), key: 'monthlyForecast', width: 12 },
        { header: this.t('평균주기'), key: 'avgCycleDays', width: 12 },
      ]
    },
    async fetchAllListRows() {
      const acc = []
      let page = 1
      const rows = 200
      let total = Infinity
      while (acc.length < total && page <= 50) {
        const data = await this.requestListPage(page, rows)
        const list = data.list || []
        total = Number(data.totalCount != null ? data.totalCount : list.length)
        acc.push(...list)
        if (!list.length || list.length < rows) break
        page += 1
      }
      return sortSupplyRows(acc, this.listSort, this.sortGetters)
    },
    async fetchAllHistoryRows() {
      if (!this.selectedItem) return []
      const acc = []
      let page = 1
      const rows = 200
      let total = Infinity
      while (acc.length < total && page <= 50) {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_HISTORY)
          .setObject({
            ...this.buildItemHistoryQuery(this.selectedItem),
            ...this.historyOrderPayload(this.historySort),
            page,
            rows,
          })
          .enqueue()
        const data = res?.data || res || {}
        const list = Array.isArray(data.list) ? data.list : []
        total = Number(data.totalCount || list.length)
        acc.push(...list)
        if (!list.length || list.length < rows) break
        page += 1
      }
      return sortSupplyRows(acc, this.historySort, this.sortGetters)
    },
    slotQueryPayload(page, rows) {
      const groupKey = String(this.query.groupKey || '').trim()
      const identityKey = groupKey.indexOf('|') >= 0 ? groupKey : ''
      const modelId = Number(this.query.printerModelId || 0)
      const accountId = Number(this.query.accountId || 0)
      const rentalId = Number(this.query.rentalcompanyId || 0)
      return {
        rentalcompanyId: rentalId > 0 ? rentalId : null,
        groupKey: identityKey,
        printerModelId: modelId > 0 ? modelId : null,
        accountId: accountId > 0 ? accountId : null,
        partNumberExact: this.query.partNumberExact,
        partNumbers: this.query.partNumbers || null,
        supplyCategory: this.query.supplyCategory || '',
        colorCode: this.query.colorCode || '',
        statusCodes: this.query.statusCodes || null,
        includeError: this.query.includeError === true,
        requirePartNumber: this.query.requirePartNumber === true,
        urgentOnly: this.query.urgentOnly === true,
        remainMin: this.query.remainMin,
        remainMax: this.query.remainMax,
        remainBucket: this.query.remainBucket || '',
        manufacturerName: this.query.manufacturerName || '',
        statusCode: this.query.statusCode || '',
        keyword: this.query.keyword || '',
        compatibilityType: this.query.compatibilityType || '',
        installedManufacturer: String(this.query.installedManufacturer || '').trim() || null,
        excludePaperRelated: this.query.excludePaperRelated !== false,
        excludeNegativeRemainError: this.query.excludeNegativeRemainError !== false,
        distinctPrinter: this.query.distinctPrinter === true,
        liveSlotQuery: true,
        unmappedOnly: !!this.query.unmappedOnly,
        lightQuery: this.query.lightQuery === true,
        orderBy: this.mapSortKey(this.listSort.key),
        orderDir: this.listSort.dir,
        page,
        rows,
      }
    },
    async requestListPage(page, rows) {
      if (this.isSlotDetail) {
        const res = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_SLOTS)
          .setObject(this.slotQueryPayload(page, rows))
          .enqueue()
        const data = res?.data || res || {}
        const list = Array.isArray(data.list) ? data.list : Array.isArray(data) ? data : []
        return { list, totalCount: Number(data.totalCount != null ? data.totalCount : list.length) }
      }
      if (this.mode === 'lowEmpty') {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject({
            rentalcompanyId: this.query.rentalcompanyId || null,
            supplyCategory: this.query.supplyCategory || '',
            partNumber: this.query.partNumber || '',
            colorCode: this.query.colorCode || '',
            excludePaperRelated: !!this.query.excludePaperRelated,
            unmappedOnly: !!this.query.unmappedOnly,
            lowEmptyOnly: true,
            page,
            rows,
          })
          .enqueue()
        const data = res?.data !== undefined ? res.data : res
        const list = Array.isArray(data) ? data : Array.isArray(data?.list) ? data.list : []
        return {
          list,
          totalCount: Number(data?.totalCount != null ? data.totalCount : list.length),
        }
      }
      if (this.mode === 'forecast') {
        const res = await this.$request(PATH.MONITORING.SUPPLY_PURCHASE_FORECAST)
          .setObject({
            rentalcompanyId: this.query.rentalcompanyId || null,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            supplyCategory: this.query.supplyCategory || '',
            partNumber: this.query.partNumber || '',
            colorCode: this.query.colorCode || '',
            excludePaperRelated: !!this.query.excludePaperRelated,
            unmappedOnly: !!this.query.unmappedOnly,
            groupBy: 'SUPPLY',
            orderBy: this.mapSortKey(this.listSort.key),
            orderDir: this.listSort.dir,
            page,
            rows,
          })
          .enqueue()
        const data = res?.data !== undefined ? res.data : res
        const list = Array.isArray(data) ? data : Array.isArray(data?.list) ? data.list : []
        return {
          list,
          totalCount: Number(data?.totalCount != null ? data.totalCount : list.length),
        }
      }
      const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_HISTORY)
        .setObject({
          ...this.query,
          ...this.historyOrderPayload(this.listSort),
          page,
          rows,
        })
        .enqueue()
      const data = res?.data || res || {}
      const list = Array.isArray(data.list) ? data.list : []
      return { list, totalCount: Number(data.totalCount || 0) }
    },
    resetToList() {
      this.view = 'list'
      this.selectedItem = null
      this.selectedHistory = null
      this.historyItems = []
      this.page = 1
      this.listSort = this.defaultListSort()
      this.historySort = { key: 'collectedDatetime', dir: 'DESC' }
      this.workspaceKind =
        this.mode === 'lowEmpty'
          ? 'lowEmpty'
          : this.mode === 'forecast'
            ? 'forecast'
            : 'history'
    },
    bindOutsideClose() {
      this.unbindOutsideClose()
      this.outsideCloseBound = (e) => this.onOutsidePointer(e)
      document.addEventListener('mousedown', this.outsideCloseBound, true)
    },
    unbindOutsideClose() {
      if (this.outsideCloseBound) {
        document.removeEventListener('mousedown', this.outsideCloseBound, true)
        this.outsideCloseBound = null
      }
    },
    onOutsidePointer(e) {
      if (!this.value) return
      if (this.$el && this.$el.contains(e.target)) return
      if (e.target?.closest?.('[data-supply-drawer-keep]')) return
      this.$emit('input', false)
    },
    close() {
      if (this.view !== 'list') {
        this.back()
        return
      }
      this.$emit('input', false)
    },
    back() {
      if (this.view === 'historyDetail') {
        if (this.mode === 'history') {
          this.view = 'list'
          this.selectedHistory = null
          return
        }
        // 품목 워크스페이스로 복귀 (이력 목록 유지)
        this.view = 'itemWorkspace'
        this.selectedHistory = null
        return
      }
      if (this.view === 'lowEmptySlots') {
        this.view = 'list'
        if (this.savedLowEmptyList) {
          this.listItems = this.savedLowEmptyList.items
          this.listTotalCount = this.savedLowEmptyList.total
          this.page = this.savedLowEmptyList.page || 1
          this.savedLowEmptyList = null
        } else {
          this.page = 1
        }
        this.slotFocus = null
        return
      }
      if (this.view === 'itemWorkspace') {
        this.view = 'list'
        this.selectedItem = null
        this.historyItems = []
        this.page = 1
        // listItems는 유지 (덮어쓰지 않음)
        return
      }
      this.$emit('input', false)
    },
    changePage(p) {
      this.page = p
      if (this.view === 'lowEmptySlots') {
        this.loadFocusedSlots()
        return
      }
      this.loadList()
    },
    skuCountLabel() {
      if (this.skuGroupBy === 'MODEL') return this.t('모델 수')
      if (this.skuGroupBy === 'ACCOUNT') return this.t('거래처 수')
      if (this.skuGroupBy === 'RENTAL') return this.t('임대업체 수')
      return this.t('SKU 수')
    },
    skuRowLabel(item) {
      if (this.skuGroupBy === 'MODEL') return item.printerModelName || item.groupLabel || '-'
      if (this.skuGroupBy === 'ACCOUNT') return item.accountName || item.groupLabel || '-'
      if (this.skuGroupBy === 'RENTAL') return item.rentalcompanyName || item.groupLabel || '-'
      return item.partNumber || this.itemLabel(item)
    },
    openSkuRowDetail(item) {
      if (!item) return
      this.savedLowEmptyList = { items: this.listItems, total: this.listTotalCount, page: this.page }
      this.slotFocus = { item, statusCodes: null, groupDetail: true }
      this.page = 1
      this.view = 'lowEmptySlots'
      this.loadFocusedSlots()
    },
    openLowEmptySlots(item, statusCodes, options) {
      const distinctPrinter = !!(options && options.distinctPrinter)
      const onlyEmpty = !distinctPrinter && statusCodes.length === 1 && statusCodes[0] === 'EMPTY'
      const onlyLow = !distinctPrinter && statusCodes.length === 1 && statusCodes[0] === 'LOW'
      const count = distinctPrinter
        ? Number(item.lowEmptyPrinterCount || 0)
        : onlyEmpty
          ? Number(item.emptyCount || 0)
          : onlyLow
            ? Number(item.lowCount || 0)
            : Number(item.lowCount || 0) + Number(item.emptyCount || 0)
      if (!count) return
      this.savedLowEmptyList = { items: this.listItems, total: this.listTotalCount, page: this.page }
      this.slotFocus = { item, statusCodes, distinctPrinter }
      this.page = 1
      this.view = 'lowEmptySlots'
      this.loadFocusedSlots()
    },
    async loadFocusedSlots() {
      if (!this.slotFocus) return
      this.loading = true
      try {
        const item = this.slotFocus.item || {}
        const part = String(item.partNumber || '').trim()
        const groupDetail = !!this.slotFocus.groupDetail
        const payload = {
          rentalcompanyId: this.query.rentalcompanyId || null,
          includeError: groupDetail,
          excludePaperRelated: !!this.query.excludePaperRelated,
          excludeNegativeRemainError: true,
          liveSlotQuery: true,
          lightQuery: true,
          distinctPrinter: !!this.slotFocus.distinctPrinter,
          page: this.page,
          rows: this.rows,
        }
        if (!groupDetail) payload.statusCodes = this.slotFocus.statusCodes
        if (groupDetail && this.skuGroupBy === 'ACCOUNT') {
          payload.accountId = Number(item.accountId || item.groupKey || 0) || null
        } else if (groupDetail && this.skuGroupBy === 'MODEL') {
          payload.printerModelId = Number(item.printerModelId || item.groupKey || 0) || null
        } else if (groupDetail && this.skuGroupBy === 'RENTAL') {
          payload.rentalcompanyId = Number(item.rentalcompanyId || item.groupKey || 0) || null
        } else if (part) {
          payload.partNumberExact = part
          if (item.supplyCategory) payload.supplyCategory = item.supplyCategory
          const color = String(item.colorCode || '').trim()
          if (color && color.toUpperCase() !== 'UNKNOWN') payload.colorCode = color
        } else {
          payload.groupKey = item.groupKey || item.marketItemKey || ''
        }
        const res = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_SLOTS)
          .setObject(payload)
          .enqueue()
        const data = res?.data || res || {}
        this.listItems = Array.isArray(data.list) ? data.list : []
        this.listTotalCount = Number(data.totalCount != null ? data.totalCount : this.listItems.length)
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
      } finally {
        this.loading = false
      }
    },
    changeHistoryPage(p) {
      this.page = p
      this.loadItemHistory()
    },
    async loadList() {
      if (this.isSlotDetail) await this.loadSlots()
      else if (this.mode === 'sku') await this.loadSkuGroups()
      else if (this.mode === 'lowEmpty') await this.loadLowEmpty()
      else if (this.mode === 'forecast') await this.loadForecast()
      else await this.loadHistoryList()
    },
    async loadSlots() {
      this.loading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_SLOTS)
          .setObject(this.slotQueryPayload(this.page, this.rows))
          .enqueue()
        const data = res?.data || res || {}
        this.listItems = Array.isArray(data.list) ? data.list : Array.isArray(data) ? data : []
        this.listTotalCount = Number(data.totalCount != null ? data.totalCount : this.listItems.length)
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
        this.$toasted?.error?.(this.t('소모품 사용 목록을 불러올 수 없습니다.'))
      } finally {
        this.loading = false
      }
    },
    async loadHistoryList() {
      this.loading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_HISTORY)
          .setObject({
            ...this.query,
            ...this.historyOrderPayload(this.listSort),
            page: this.page,
            rows: this.rows,
          })
          .enqueue()
        const data = res?.data || res || {}
        this.listItems = Array.isArray(data.list) ? data.list : []
        this.listTotalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
        this.$toasted.error(this.t('교체 이력을 불러올 수 없습니다.'))
      } finally {
        this.loading = false
      }
    },
    async loadSkuGroups() {
      this.loading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject({
            rentalcompanyId: this.query.rentalcompanyId || null,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            keyword: this.query.keyword || '',
            groupBy: this.query.groupBy || 'SUPPLY',
            excludePaperRelated: !!this.query.excludePaperRelated,
            requirePartNumber: this.query.requirePartNumber === true,
            unmappedOnly: !!this.query.unmappedOnly,
            page: this.page,
            rows: this.rows,
          })
          .enqueue()
        const data = res?.data !== undefined ? res.data : res
        const list = Array.isArray(data) ? data : Array.isArray(data?.list) ? data.list : []
        this.listItems = list
        this.listTotalCount = Number(data?.totalCount != null ? data.totalCount : list.length)
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
      } finally {
        this.loading = false
      }
    },
    async loadLowEmpty() {
      this.loading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject({
            rentalcompanyId: this.query.rentalcompanyId || null,
            supplyCategory: this.query.supplyCategory || '',
            partNumber: this.query.partNumber || '',
            colorCode: this.query.colorCode || '',
            excludePaperRelated: !!this.query.excludePaperRelated,
            unmappedOnly: !!this.query.unmappedOnly,
            lowEmptyOnly: true,
            page: 1,
            rows: 200,
          })
          .enqueue()
        const data = res?.data !== undefined ? res.data : res
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.list)
            ? data.list
            : []
        this.listItems = list.filter(
          (r) => Number(r.lowCount || 0) + Number(r.emptyCount || 0) > 0
        )
        this.listTotalCount = this.listItems.length
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
      } finally {
        this.loading = false
      }
    },
    async loadForecast() {
      this.loading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_PURCHASE_FORECAST)
          .setObject({
            rentalcompanyId: this.query.rentalcompanyId || null,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            supplyCategory: this.query.supplyCategory || '',
            partNumber: this.query.partNumber || '',
            excludePaperRelated: !!this.query.excludePaperRelated,
            groupBy: 'SUPPLY',
            orderBy: this.mapSortKey(this.listSort.key),
            orderDir: this.listSort.dir,
            page: 1,
            rows: 100,
          })
          .enqueue()
        this.listItems = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : []
        this.listTotalCount = this.listItems.length
      } catch (e) {
        console.error(e)
        this.listItems = []
        this.listTotalCount = 0
      } finally {
        this.loading = false
      }
    },
    buildItemHistoryQuery(item) {
      return {
        rentalcompanyId: this.query.rentalcompanyId || null,
        fromDate: this.query.fromDate,
        toDate: this.query.toDate,
        groupKey: item.groupKey || item.marketItemKey || null,
        excludePaperRelated: !!this.query.excludePaperRelated,
        lightQuery: this.query.lightQuery === true,
      }
    },
    async openItemWorkspace(item, kind) {
      this.selectedItem = item
      this.workspaceKind = kind
      this.selectedHistory = null
      this.page = 1
      this.view = 'itemWorkspace'
      await this.loadItemHistory()
    },
    async loadItemHistory() {
      if (!this.selectedItem) return
      this.historyLoading = true
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_HISTORY)
          .setObject({
            ...this.buildItemHistoryQuery(this.selectedItem),
            ...this.historyOrderPayload(this.historySort),
            page: this.page,
            rows: this.rows,
          })
          .enqueue()
        const data = res?.data || res || {}
        this.historyItems = Array.isArray(data.list) ? data.list : []
        this.historyTotalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.historyItems = []
        this.historyTotalCount = 0
      } finally {
        this.historyLoading = false
      }
    },
    openHistoryDetail(item) {
      this.selectedHistory = item
      this.view = 'historyDetail'
    },
    itemLabel(item) {
      if (!item) return '-'
      return (
        item.supplyLabel ||
        item.groupLabel ||
        [item.manufacturerName, item.printerModelName, item.description]
          .filter(Boolean)
          .join(' · ') ||
        item.description ||
        '-'
      )
    },
    supplyDesc(item) {
      const desc = item?.description ? String(item.description).trim() : ''
      return desc || '-'
    },
    printerLabel(item) {
      const model = item?.printerModelName || '-'
      const serial = item?.printerSerialNo || ''
      return serial ? `${model} [${serial}]` : model
    },
    formatRemain(item) {
      return this.formatPercent(item?.remainPercent)
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `${value}%`
    },
    formatForecast(val) {
      if (val === null || val === undefined || val === '') {
        return this.t('데이터 부족')
      }
      return Number(val).toLocaleString()
    },
    hasBeforeCollect(item) {
      return !!(
        item &&
        (item.beforeHistId ||
          item.beforeRemainPercent === 0 ||
          item.beforeRemainPercent)
      )
    },
    formatChangeReason(item) {
      const reason = item?.changeReason || ''
      const labels = {
        USAGE_REPLACE: this.t('소진 후 잔량 회복'),
        PART_CHANGE: this.t('품번 변경'),
        DESC_CHANGE: this.t('설명 변경'),
        PERCENT_JUMP: this.t('잔량 급상승'),
        STATUS_RECOVERY: this.t('상태 회복'),
        UNKNOWN: this.t('판정 근거 확인 필요'),
      }
      return labels[reason] || reason || '-'
    },
    formatCategory(cat) {
      const map = {
        TONER: this.t('토너'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      return map[cat] || cat || '-'
    },
  },
}
</script>

<style scoped>
.supply-history-drawer {
  z-index: 300 !important;
  box-shadow: -4px 0 16px rgba(15, 23, 42, 0.12);
}
.supply-history-drawer >>> .v-navigation-drawer__content {
  height: 100% !important;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}
.drawer-shell {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.drawer-head {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.drawer-title-wrap {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}
.drawer-title {
  display: block;
  height: 24px;
  font-size: 16px;
  font-weight: 700;
  font-synthesis: none;
  letter-spacing: 0;
  line-height: 24px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.drawer-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drawer-back {
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  padding: 0 0 4px;
  cursor: pointer;
}
.drawer-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.excel-btn {
  white-space: nowrap;
}
.drawer-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.drawer-table th.sortable:hover {
  color: #2563eb;
}
.drawer-close {
  border: 0;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
}
.drawer-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 12px 16px 20px;
}
.drawer-empty {
  padding: 40px 12px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
.drawer-empty.compact {
  padding: 16px 8px;
}
.section-title {
  margin: 16px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}
.drawer-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.drawer-table th,
.drawer-table td {
  border-bottom: 1px solid #eef2f7;
  padding: 8px 6px;
  text-align: left;
  vertical-align: top;
}
.drawer-table td.col-datetime,
.drawer-table td.col-remain,
.drawer-table th.nowrap {
  white-space: nowrap;
}
.drawer-table td.col-datetime {
  min-width: 150px;
}
.drawer-table td.col-remain {
  min-width: 64px;
}
.drawer-table td.desc-cell {
  max-width: 180px;
}
.drawer-table .desc-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drawer-table th {
  color: #6b7280;
  font-weight: 600;
  background: #f8fafc;
  position: sticky;
  top: 0;
}
.drawer-table tr.clickable {
  cursor: pointer;
}
.drawer-table tr.clickable:hover {
  background: #f0f9ff;
}
.cell-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0c98fe;
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  max-width: 100%;
}
.cell-link:disabled {
  color: inherit;
  text-decoration: none;
  cursor: default;
}
.drawer-table td.num-cell {
  width: 72px;
  padding: 0;
  text-align: center;
  vertical-align: middle;
}
.num-link {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  margin: 0;
  padding: 10px 8px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: 1.2;
  cursor: pointer;
}
.num-link:disabled {
  cursor: default;
}
.drawer-paging {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 12px;
}
.page-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.detail-list {
  margin: 0;
}
.detail-list.compact .detail-row {
  grid-template-columns: 90px 1fr;
}
.detail-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}
.detail-row dt {
  margin: 0;
  color: #6b7280;
}
.detail-row dd {
  margin: 0;
  color: #111827;
  word-break: break-all;
}
.compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
}
.compare-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px 6px;
  background: #fff;
  min-width: 0;
}
.compare-card.before {
  background: #f8fafc;
}
.compare-card.after {
  background: #f0f9ff;
  border-color: #bae6fd;
}
.compare-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}
.compare-empty {
  padding: 18px 4px;
  color: #94a3b8;
  font-size: 12px;
  text-align: center;
}
</style>
